import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { fmtEUR, fmtNum, monthLabelIT } from "@/lib/format";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Loader2, ArrowUp, ArrowDown, Minus } from "lucide-react";

function DeltaPill({ value, suffix = "" }) {
  if (value == null) return <span className="text-slate-400">—</span>;
  const positive = value > 0;
  const zero = value === 0;
  const Icon = zero ? Minus : positive ? ArrowUp : ArrowDown;
  const cls = zero ? "text-slate-500 bg-slate-100" : positive ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[3px] text-xs font-medium tabular-nums ${cls}`}>
      <Icon className="w-3 h-3" /> {Math.abs(value).toLocaleString("it-IT", { maximumFractionDigits: 1 })}{suffix}
    </span>
  );
}

function KpiBox({ label, current, previous, delta, formatter, deltaSuffix = "", testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-[3px] p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="font-serif text-3xl font-medium text-slate-900 tabular-nums mt-2">{formatter(current)}</div>
      <div className="mt-2 text-xs text-slate-500 flex items-center gap-2">
        Mese precedente: <span className="tabular-nums">{formatter(previous)}</span>
        <DeltaPill value={delta} suffix={deltaSuffix} />
      </div>
    </div>
  );
}

export default function ComparePage() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);

  useEffect(() => {
    api.get("/dashboard/compare", { params: { months } }).then((r) => setData(r.data));
  }, [months]);

  if (!data) return <AppLayout title="Confronto Mese su Mese"><Loader2 className="w-5 h-5 animate-spin" /></AppLayout>;

  const series = data.series || [];
  const cur = series[series.length - 1] || {};
  const prev = series[series.length - 2] || {};
  const chartData = series.map((s) => ({ ...s, label: monthLabelIT(s.month).slice(0, 3) }));

  return (
    <AppLayout
      title="Confronto Mese su Mese"
      subtitle="Performance comparate"
      actions={
        <select value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="months-select" className="bg-white border border-slate-300 rounded-[3px] px-3 py-2 text-sm">
          <option value={3}>3 mesi</option>
          <option value={6}>6 mesi</option>
          <option value={12}>12 mesi</option>
          <option value={24}>24 mesi</option>
        </select>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in">
        <KpiBox testid="kpi-prov" label="Provvigioni mese" current={cur.provvigioni || 0} previous={prev.provvigioni || 0} delta={data.delta?.provvigioniPct} deltaSuffix="%" formatter={fmtEUR} />
        <KpiBox testid="kpi-mont" label="Montante mese" current={cur.montante || 0} previous={prev.montante || 0} delta={data.delta?.montantePct} deltaSuffix="%" formatter={fmtEUR} />
        <KpiBox testid="kpi-conv" label="Conversion rate" current={cur.conversion || 0} previous={prev.conversion || 0} delta={(cur.conversion || 0) - (prev.conversion || 0)} deltaSuffix=" pp" formatter={(v) => `${fmtNum(v, 1)}%`} />
        <KpiBox testid="kpi-new" label="Nuovi clienti" current={cur.newClients || 0} previous={prev.newClients || 0} delta={(cur.newClients || 0) - (prev.newClients || 0)} formatter={(v) => fmtNum(v)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        <div className="bg-white border border-slate-200 rounded-[3px] p-5">
          <h3 className="font-serif text-base text-slate-900 mb-3">Provvigioni & Montante</h3>
          <div style={{ width: "100%", height: 280, minWidth: 100, minHeight: 200 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={200}>
              <BarChart data={chartData}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }} formatter={(v) => fmtEUR(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="provvigioni" fill="#B7955B" name="Provvigioni" />
                <Bar dataKey="montante" fill="#0A192F" name="Montante" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-[3px] p-5">
          <h3 className="font-serif text-base text-slate-900 mb-3">Conversion rate (%)</h3>
          <div style={{ width: "100%", height: 280, minWidth: 100, minHeight: 200 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={200}>
              <LineChart data={chartData}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }} formatter={(v) => `${v}%`} />
                <Line type="monotone" dataKey="conversion" stroke="#B7955B" strokeWidth={2.5} dot={{ r: 4, fill: "#B7955B" }} name="Conversion %" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-5 bg-white border border-slate-200 rounded-[3px] overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#F0F2F5]">
          <h3 className="font-serif text-base text-slate-900">Tabella mese su mese</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.1em] text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="text-left font-semibold py-2.5 px-4">Mese</th>
              <th className="text-right font-semibold py-2.5 px-4">Nuovi clienti</th>
              <th className="text-right font-semibold py-2.5 px-4">Liquidate</th>
              <th className="text-right font-semibold py-2.5 px-4">Pratiche tot.</th>
              <th className="text-right font-semibold py-2.5 px-4">Conversion</th>
              <th className="text-right font-semibold py-2.5 px-4">Montante</th>
              <th className="text-right font-semibold py-2.5 px-4">Provvigioni</th>
            </tr>
          </thead>
          <tbody>
            {series.map((s) => (
              <tr key={s.month} className="border-b border-[#F0F2F5] tbl-row">
                <td className="py-2.5 px-4 capitalize text-slate-900 font-medium">{monthLabelIT(s.month)}</td>
                <td className="py-2.5 px-4 text-right tabular-nums">{s.newClients}</td>
                <td className="py-2.5 px-4 text-right tabular-nums">{s.liquidate}</td>
                <td className="py-2.5 px-4 text-right tabular-nums">{s.totalPractices}</td>
                <td className="py-2.5 px-4 text-right tabular-nums">{fmtNum(s.conversion, 1)}%</td>
                <td className="py-2.5 px-4 text-right tabular-nums">{fmtEUR(s.montante)}</td>
                <td className="py-2.5 px-4 text-right tabular-nums text-[#8a6e3d] font-medium">{fmtEUR(s.provvigioni)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
